<template>
    <form @submit.prevent="onSubmit">
        <div class="modal-main">
            <h3>Send Message</h3>
            <div class="checkbox" v-if="!pagesWithPrimaryOrSecondaryParentOption.includes(this.$route.path)">
                <p class="checkbox__select-label">Select who will receive the email</p>
                <div class="checkbox__input">
                    <input 
                        type="checkbox" 
                        id="primary" 
                        value="primary" 
                        v-model="entitySelected">
                    <label 
                        for="primary">
                        Primary Contact
                    </label>
                    <input 
                        type="checkbox" 
                        id="secondary" 
                        value="secondary" 
                        v-model="entitySelected">
                    <label 
                        for="secondary">
                        Secondary Contact
                    </label>
                </div>
            </div>
            <p class="checkbox__select-label copy__email"
                v-if="!pagesWithCopyNote.includes(this.$route.path)">
                CC: {{ userInfo.email }}
            </p>
            <app-input id="subject"
                       label="Subject"
                       name="subject"
                       type="text"
                       v-validate="'required|min:1|max:100'"
                       v-model="form.subject">
            </app-input>
            <text-editor name="editor"
                         :height=height
                         v-model="form.message"
                         v-validate="'required|max:8000'"
                         @input="form.message = $event">
            </text-editor>
            <div class="input-container" v-if="!pagesWithAttachmentOption.includes(this.$route.path)">
                <div class="file-input__container">
                    <div v-for="file in this.attachment" :key="file.name" class="file-item">
                        {{ file.name }}
                        <div class="file-item__remove">
                            <img 
                                src="../../assets/img/close.svg"
                                class="file-item__remove-img"
                                @click.stop="removeFileFromList(file)"
                            >
                        </div>
                    </div>
                </div>
                <email-file-input
                    style="margin-top:-20px"
                    id="file"
                    name="attachment"
                    :text="`${this.fileInputText}`"
                    btn-text="Choose file"
                    ref="fileInput"
                    :hide-error="true"
                    @change="handleFilesUpload($event)"
                />
                <div v-if="showFileSizeError" class="error" style="margin-top: -20px;">Total file size should be less than 20Mb</div>
            </div>
            <div v-show="fields.editor && (fields.editor.touched ||
                        fields.editor.validated)"
                 class="error">
                {{ errors.first('editor') }}
            </div>
        </div>
        <div class="form-footer">
            <button class="primary-btn btn-center"
                    type="submit"
                    :disabled="!formValid || formPristine || isBothCheckboxEmpty || !isFileValid"
                    v-spinner="isLoading">Send
            </button>
        </div>
    </form>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import { FormMixin } from '@/mixins/FormMixin';
    import SpinnerDirective from '@/directives/SpinnerDirective';

    export default {
        name: 'SendEmailModal',
        components: {
            AppInput: () => import('@/components/controls/AppInput'),
            TextEditor: () => import('@/components/controls/TextEditor'),
            EmailFileInput: () => import('@/components/controls/EmailFileInput'),
        },
        directives: { SpinnerDirective },
        mixins: [FormMixin],
        data() {
            return {
                form: {
                    subject: null,
                    message: null,
                },
                isLoading: false,
                attachment: [],
                entitySelected: ['primary'],
                mailIds: null,
                pagesWithPrimaryOrSecondaryParentOption: ["/settings/users", "/settings/email_history"],
                pagesWithAttachmentOption: ["/settings/email_history"],
                pagesWithCopyNote: ["/settings/email_history"],
                fileInputText: 'Add file here',
                isFileValid: true,
                showFileSizeError: false,
            }
        },
        computed: {
            ...mapGetters([
                'isMobile',
                'selected',
                'schoolId',
                'options',
                'resendEmail',
                'userInfo'
            ]),
            height() {
                return this.isMobile ? '350px' : '450px';
            },
            isBothCheckboxEmpty() {
                return this.entitySelected.length === 0;
            }
        },
        methods: {
            ...mapActions([
                'createSuccessNotification',
                'resetSelectedItems',
                'getUserInfo',
                'resetResendEmail'
            ]),
            checkFileSizeValidity() {
                let filesSize = 0;
                Object.values(this.attachment).forEach((val) => {
                    filesSize = filesSize + val.size;
                });
                if (filesSize > 20971520) {
                    this.isFileValid = false;
                    this.showFileSizeError = true;
                } else {
                    this.isFileValid = true;
                    this.showFileSizeError = false;
                }
            },
            handleFilesUpload(event){
                this.fileInputText = 'Add file here';
                let filesSize = 0;
                Object.values(this.attachment).forEach((val) => {
                    filesSize = filesSize + val.size;
                });
                if (( filesSize + event.size ) < 20971520) {
                    this.isFileValid = true;
                    this.showFileSizeError = false;
                } else {
                    this.isFileValid = false;
                    this.showFileSizeError = true;
                }
                this.attachment.push(event)
            },
            removeFileFromList(file) {
                this.attachment.splice(this.attachment.indexOf(file), 1);
                this.checkFileSizeValidity();
            },
            onSubmit() {
                if (!this.isLoading) {
                    this.$validator.validate().then(valid => {
                        if (valid) {
                            this.isLoading = true;
                            const { id } = this.options;
                            this.mailIds = id ? [id]: this.selected;
                            if (!this.pagesWithPrimaryOrSecondaryParentOption.includes(this.$route.path)) {
                                if (this.entitySelected.includes("secondary")) {
                                    this.form.secondaryEntityIds = id ? [id]: this.selected;
                                }
                                if (!this.entitySelected.includes("primary")) {
                                    this.mailIds = [];
                                } 
                            }
                            this.useService(this.form, this.mailIds, this.attachment, this.entitySelected)
                                .then(() => {
                                    this.$emit('submit');
                                    this.resetSelectedItems();
                                    this.createSuccessNotification('Email was successfully sent');
                                })
                                .finally(() => this.isLoading = false);
                        }
                    });
                }
            },
            // the methods will be overwritten from inherited component
            useService() {},
        },
        beforeMount() {
            if(this.resendEmail) {
                this.form = {
                    subject: this.resendEmail.subject,
                    message: this.resendEmail.message,
                    id: this.resendEmail.id,
                    schoolId: this.schoolId,
                }
            }
        },
        beforeDestroy() {
            if(this.resendEmail) {
                this.resetResendEmail();
            }
        },
        async created() {
            await this.getUserInfo();
        },
    }
</script>
<style scoped>
    .error {
        position: relative;
    }

    .custom-control-label {
        padding-top: 2px !important;
    }

    .checkbox {
        margin-bottom: 15px;
        font-family: Asap;
        font-size: 16px;
        color: #888b94;
    }

    #secondary {
        margin-left: 15px;
    }

    .checkbox__input {
        display: flex;
        align-items: center;
    }

    .checkbox__input label {
        margin-left: 26px;
        color: rgba(25,24,28,1);
        font-family: "Asap";
        font-size: 15px;
        font-weight: 400;
        font-style: normal;
        letter-spacing: 0px;
        text-align: left;
        line-height: 24px;
    }

    .checkbox__select-label {
        color: rgba(25,24,28,1);
        margin-bottom: 15px;
    }

    .input-container {
        margin-top: 75px;
    }

    input[type=checkbox] {
        position: relative;
	    cursor: pointer;
        border: none !important;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 24px;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
    }

    input[type=checkbox]:before {
        content: "";
        display: block;
        position: absolute;
        width: 18px;
        height: 18px;
        top: 0;
        left: 0;
        border: 2px solid #e9edf0;
    }

    input[type=checkbox]:before:hover {
        background-color: #f7f8f9 !important;
    }
    
    input[type=checkbox]:checked:before {
        content: "";
        display: block;
        position: absolute;
        width: 18px;
        height: 18px;
        top: 0;
        left: 0;
        background-color: #64c8ff !important;
        border: 2px solid #64c8ff;
    }
    
    input[type=checkbox]:checked:after {
        content: "";
        display: block;
        width: 8px;
        height: 12px;
        border: solid white;
        border-width: 0 2px 2px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
        position: absolute;
        top: 1px;
        left: 5px;
    }

    .file-input__container {
        z-index: 9999;
        position: relative;
    }

    .file-item {
        border-radius: 24px;
        background-color: rgba(247,248,249, 1);
        color: rgba(35,21,48,1);
        font-family: "Asap";
        font-size: 14px;
        font-weight: 400;
        font-style: normal;
        letter-spacing: 0px;
        text-align: left;
        padding: 7px;
        display: flex;
        width: -moz-fit-content;
        width: fit-content;
        margin-top: 5px;
        transition: 0.5s ease-in-out;
    }

    .file-item:hover {
        background-color: #b496fa;
        color: rgba(255,255,255,1);
        cursor: pointer;
    }

    .file-item__remove {
        background-color: rgba(255,255,255, 1);
        margin-left: 10px;
        z-index: 1010;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        width: 20px;
        height: 20px;
    }

    .copy__email {
        color: #888b94;
    }

    @media only screen and (max-width: 569px) {
        .input-container {
            margin-top: 110px;
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/modals/SendEmailModal.vue